//#region IMPORT
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  IconButton,
  TextField,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Avatar,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  AccountTree as AccountTreeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TouchApp as TouchAppIcon,
  Mouse as MouseIcon,
  HelpOutline as HelpOutlineIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Dialog, Button } from '../../../../Components/MUIs';
import SAP_BASIC_01_OrgFlow from './SAP_BASIC_01_OrgFlow';
//#endregion

/** 엑셀보내기용 기본 부서 카드 크기 */
const ORG_CHART_DEPT_CARD_DEFAULT_W = 188;
const ORG_CHART_DEPT_CARD_DEFAULT_H = 120;

/** 조직도 캔버스 조작 안내 (하단 바·툴팁 공통) */
const ORG_CHART_CANVAS_HELP_LINES = [
  '마우스 휠 : 조직도 화면 위·아래 이동',
  'Shift + 마우스 휠 : 조직도 화면 좌·우 이동',
  'Ctrl(⌘) + 마우스 휠 : 화면 확대/축소 (Mac 트랙패드 핀치 가능)',
  '우클릭 후 드래그 : 조직도 전체 위치 이동',
  '부서 카드 드래그 : 부서 위치 이동',
  '카드 테두리 연결점(●) 드래그 : 다른 부서와 연결',
  '연결선 클릭 선택 후 Delete 키 : 연결선 삭제',
];
const ORG_CHART_LINE_HELP_LINES = [
  '부서 카드 테두리의 연결점(●)을 다른 부서 쪽으로 드래그하면 연결선이 만들어집니다.',
  '만든 연결선을 클릭해 선택한 뒤 Delete(또는 Backspace) 키를 누르면 삭제됩니다.',
  '',
  '[화면 이동·확대]',
  ...ORG_CHART_CANVAS_HELP_LINES.slice(0, 4),
];

function SAP_BASIC_01(props) {
  //#region 변수
  const orgFlowRef = useRef(null);

  // 부서 추가 다이얼로그
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [departmentInput, setDepartmentInput] = useState({
    name: '',
  });

  // 인력 추가 다이얼로그
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [memberInput, setMemberInput] = useState({
    name: '',
    position: '',
    depNm: '',
  });
  // 사이드바 구성원 탭 목록 검색 (성명/직책)
  const [sidebarMemberSearchText, setSidebarMemberSearchText] = useState('');
  const sidebarFilteredMemberList = (props.memberList || []).filter((m) => {
    if (!sidebarMemberSearchText.trim()) return true;
    const q = sidebarMemberSearchText.trim().toLowerCase();
    const name = (m.name ?? m.NAME ?? '').toString().toLowerCase();
    const position = (m.position ?? m.POSITION ?? '').toString().toLowerCase();
    return name.includes(q) || position.includes(q);
  });

  // 타 부서 직원 검색
  const [memberSearchText, setMemberSearchText] = useState('');
  const [searchedMembers, setSearchedMembers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  /** 연결선 스타일: straight | smooth | grid */
  const [lineType, setLineType] = useState('straight');
  const [curveness] = useState(0.5);

  /** 캔버스 조작 안내 — 기본 접힘(캔버스 가림 방지), 펼침 상태는 sessionStorage 유지 */
  const [canvasHelpOpen, setCanvasHelpOpen] = useState(() => {
    try {
      return sessionStorage.getItem('sap_basic_01_canvas_help_open') === 'true';
    } catch {
      return false;
    }
  });
  const ToggleCanvasHelp = useCallback(() => {
    setCanvasHelpOpen((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem('sap_basic_01_canvas_help_open', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (props.saveClick1) {
      if (!props.validateSelection()) {
        props.setSaveClick1(false);
        return;
      }

      props.Save1();
      props.setSaveClick1(false);
    }
  }, [props.saveClick1]);
  // 탭 상태
  const [sidebarTab, setSidebarTab] = useState(0);

  // 헤더 추가 메뉴 (Fab 클릭 시)
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  //#endregion

  //#region 드래그 앤 드롭 핸들러 (사이드바 → React Flow 캔버스)
  const handleDragStart = useCallback((e, item, type) => {
    e.dataTransfer.effectAllowed = 'move';
    // 드래그로 넘기는 데이터에 수기 여부 플래그 포함
    // - 요청사항: item.id가 null/undefined면 isManual='Y', 아니면 'N'
    const itemToSend =
      type === 'member'
        ? {
            ...item,
            isManual: item?.isManual,
          }
        : item;

    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({ item: itemToSend, type })
    );
  }, []);

  //#region 부서 추가 핸들러
  const handleAddDepartmentClick = () => {
    setDepartmentInput({ name: '' });
    setDepartmentDialogOpen(true);
  };

  const handleDepartmentSave = () => {
    if (!departmentInput.name || !departmentInput.name.trim()) {
      return;
    }
    const timestamp = Date.now();
    const randomStr =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    const newDepartment = {
      depCd: `MANUAL_${timestamp}_${randomStr}`,
      depNmLast: departmentInput.name.trim(),
      name: departmentInput.name.trim(),
      isManual: 'Y', // 수기 입력 구분
    };

    props.onDepartmentListAdd(newDepartment);
    setDepartmentDialogOpen(false);
    setDepartmentInput({ name: '' });
  };
  //#endregion

  //#region 인력 추가 핸들러
  const handleAddMemberClick = () => {
    setMemberInput({ name: '', position: '', depNm: '' });
    setMemberDialogOpen(true);
  };

  const handleMemberSave = () => {
    if (!memberInput.name || !memberInput.name.trim()) {
      return;
    }
    const newMember = {
      id: `MANUAL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: memberInput.name.trim(),
      position: memberInput.position?.trim() || '',
      depNm: memberInput.depNm?.trim() || '',
      isManual: 'Y',
    };
    props.onMemberListAdd(newMember);
    setMemberDialogOpen(false);
    setMemberInput({ name: '', position: '', depNm: '' });
  };
  //#endregion

  //#region 타 부서 직원 검색
  const handleMemberSearch = async () => {
    if (!memberSearchText || !memberSearchText.trim()) {
      setSearchedMembers([]);
      return;
    }

    if (!props.onSearchMembers) return;

    setIsSearching(true);
    try {
      await props.onSearchMembers(memberSearchText.trim(), (results) => {
        setSearchedMembers(results);
      });
    } catch (error) {
      console.error('인력 검색 오류:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleMemberSearch();
    }
  };
  //#endregion

  // 조직도를 이미지로 변환: html-to-image(toPng) 우선(SVG 꺾은선 안정), 실패 시 html2canvas
  // 이미지 다운로드 함수
  const downloadImage = useCallback((imageDataUrl, fileName) => {
    if (!imageDataUrl) return;

    try {
      // Data URL을 Blob으로 변환 (fetch 사용)
      fetch(imageDataUrl)
        .then((res) => res.blob())
        .then((blob) => {
          // Object URL 생성
          const url = window.URL.createObjectURL(blob);

          // 다운로드 링크 생성 및 클릭
          const a = document.createElement('a');
          a.href = url;
          a.download =
            fileName ||
            `조직도_${new Date().toISOString().replace(/T/, '_').replace(/\..+/, '').replace(/-/g, '').replace(/:/g, '')}.png`;
          document.body.appendChild(a);
          a.click();

          // 정리
          setTimeout(() => {
            a.remove();
            window.URL.revokeObjectURL(url);
          }, 100);
        })
        .catch((e) => {
          console.error('이미지 다운로드 중 오류 발생:', e);
        });
    } catch (e) {
      console.error('이미지 다운로드 중 오류 발생:', e);
    }
  }, []);

  const generateOrganizationImage = useCallback(async () => {
    if (!orgFlowRef.current?.captureImage) {
      console.error('orgFlowRef가 없습니다.');
      return null;
    }
    try {
      return await orgFlowRef.current.captureImage();
    } catch (error) {
      console.error('이미지 생성 오류:', error);
      return null;
    }
  }, []);

  const DownloadOrganizationExcel = useCallback(async () => {
    const org = props.organizationData || {};
    const departments = Array.isArray(org.departments) ? org.departments : [];
    const members = Array.isArray(org.members) ? org.members : [];
    const lines = Array.isArray(org.lines) ? org.lines : [];

    const StripDeptPrefix = (v) =>
      v == null ? '' : String(v).replace(/^dept[-_]/i, '');

    const RectIntersects = (a, b) =>
      !(a.c2 < b.c1 || a.c1 > b.c2 || a.r2 < b.r1 || a.r1 > b.r2);

    const GetDeptCanvasXY = (dept) => {
      const x = Number.isFinite(Number(dept.x)) ? Math.round(Number(dept.x)) : 0;
      const y = Number.isFinite(Number(dept.y)) ? Math.round(Number(dept.y)) : 0;
      return { x, y };
    };

    const GetDeptRect = (dept) => {
      const { x, y } = GetDeptCanvasXY(dept);
      return {
        x,
        y,
        w: ORG_CHART_DEPT_CARD_DEFAULT_W,
        h: ORG_CHART_DEPT_CARD_DEFAULT_H,
      };
    };

    try {
      const xlsxMod = await import('xlsx');
      const XLSX = xlsxMod.default ?? xlsxMod;

      const EscapeHtml = (raw) =>
        String(raw ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      const tipText =
        '아래 격자는 화면 조직도와 비슷한 비율로 배치한 부서 카드입니다. 병합된 카드 안에서 부서명·인력을 편집할 수 있습니다. 카드 사이 빈 칸에는 연결선을 박스 문자(─ │ 등)로 표시합니다.';

      if (departments.length === 0) {
        const wrap0 = document.createElement('div');
        wrap0.innerHTML = `<table><tr><td>${EscapeHtml(
          '캔버스에 배치된 부서가 없습니다. 조직도를 만든 뒤 다시 시도하세요.'
        )}</td></tr></table>`;
        const tbl0 = wrap0.querySelector('table');
        const wb0 = XLSX.utils.book_new();
        const ws0 = XLSX.utils.table_to_sheet(tbl0, { raw: true });
        XLSX.utils.book_append_sheet(wb0, ws0, '조직도');
        XLSX.writeFile(
          wb0,
          `조직도_${new Date().toISOString().replace(/T/, '_').replace(/\..+/, '').replace(/-/g, '').replace(/:/g, '')}.xlsx`
        );
        return;
      }

      let canvasW = 800;
      let canvasH = 600;
      departments.forEach((dept) => {
        const r = GetDeptRect(dept);
        canvasW = Math.max(canvasW, r.x + r.w + 32);
        canvasH = Math.max(canvasH, r.y + r.h + 32);
      });

      const maxGridCols = 72;
      const maxGridRows = 96;
      const pxPerCol = canvasW / maxGridCols;
      const pxPerRow = canvasH / maxGridRows;

      const gridFirstRow = 2;
      const lastGridRow = gridFirstRow + maxGridRows - 1;

      const nRows = lastGridRow;
      const nCols = maxGridCols;
      const anchor = Array.from({ length: nRows }, () => Array(nCols).fill(null));
      const skip = Array.from({ length: nRows }, () => Array(nCols).fill(false));
      const tdText = Array.from({ length: nRows }, () => Array(nCols).fill(''));

      anchor[0][0] = {
        text: EscapeHtml(tipText).replace(/\n/g, '<br/>'),
        rs: 1,
        cs: nCols,
        kind: 'tip',
      };
      for (let cc = 1; cc < nCols; cc += 1) {
        skip[0][cc] = true;
      }

      const placed = [];
      const sortKey = (d) => {
        const { x, y } = GetDeptRect(d);
        return y * 100000 + x;
      };
      const sorted = [...departments].sort((a, b) => sortKey(a) - sortKey(b));

      sorted.forEach((dept) => {
        const { x, y, w, h } = GetDeptRect(dept);
        let c1 = Math.max(1, Math.floor(x / pxPerCol) + 1);
        let r1 = gridFirstRow + Math.floor(y / pxPerRow);
        r1 = Math.max(gridFirstRow, Math.min(lastGridRow - 2, r1));
        let c2 = Math.min(
          maxGridCols,
          Math.max(c1 + 2, Math.ceil((x + w) / pxPerCol))
        );
        let r2 = gridFirstRow + Math.ceil((y + h) / pxPerRow) - 1;
        r2 = Math.max(r1 + 2, Math.min(lastGridRow, r2));

        let rect = { r1, c1, r2, c2 };
        for (let guard = 0; guard < 120; guard += 1) {
          const box = { r1, c1, r2, c2 };
          if (!placed.some((p) => RectIntersects(p, box))) {
            rect = box;
            break;
          }
          if (c2 > c1) {
            c2 -= 1;
          } else if (r2 > r1) {
            r2 -= 1;
          } else {
            c1 = Math.min(maxGridCols - 3, c1 + 1);
            c2 = Math.min(maxGridCols, c1 + 3);
            r2 = Math.min(lastGridRow, r1 + 3);
          }
          rect = { r1, c1, r2, c2 };
        }

        if (placed.some((p) => RectIntersects(p, rect))) {
          let shifted = false;
          for (let s = 1; s <= 24 && !shifted; s += 1) {
            const tryRect = {
              r1: rect.r1,
              c1: rect.c1 + s,
              r2: Math.min(lastGridRow, rect.r1 + 3),
              c2: Math.min(maxGridCols, rect.c1 + s + 4),
            };
            if (tryRect.c2 > maxGridCols) break;
            if (!placed.some((p) => RectIntersects(p, tryRect))) {
              Object.assign(rect, tryRect);
              shifted = true;
            }
          }
          if (!shifted) {
            rect = { r1, c1, r2: r1, c2: c1 };
          }
        }

        const depCd = String(dept.depCd ?? dept.DEP_CD ?? '').trim();
        const depNm = String(
          dept.depNmLast ?? dept.DEP_NM_LAST ?? dept.name ?? ''
        ).trim();
        const deptKey =
          depCd || StripDeptPrefix(String(dept.id ?? '')) || String(dept.id ?? '');

        const memberLines = members
          .filter((m) => {
            const mid =
              m.departmentId ??
              m.DepartmentId ??
              m.DEP_CD ??
              m.depCd ??
              '';
            const mStrip = StripDeptPrefix(String(mid));
            return (
              mStrip === StripDeptPrefix(String(deptKey)) ||
              String(mid) === String(dept.id)
            );
          })
          .map((m) => {
            const nm = m.NAME ?? m.name ?? '';
            const pos = m.POSITION ?? m.position ?? '';
            return pos ? `${nm} (${pos})` : `${nm}`;
          });

        const title = depNm || '(부서명 없음)';
        const bodyParts = [title];
        if (memberLines.length > 0) {
          bodyParts.push('');
          memberLines.forEach((t) => bodyParts.push(`· ${t}`));
        }
        const body = bodyParts.join('\n');

        const zr1 = rect.r1 - 1;
        const zc1 = rect.c1 - 1;
        const rs = rect.r2 - rect.r1 + 1;
        const cs = rect.c2 - rect.c1 + 1;
        if (
          zr1 < 0 ||
          zc1 < 0 ||
          zr1 + rs > nRows ||
          zc1 + cs > nCols
        ) {
          return;
        }

        anchor[zr1][zc1] = {
          text: EscapeHtml(body).replace(/\n/g, '<br/>'),
          rs,
          cs,
          kind: 'dept',
        };
        for (let dr = 0; dr < rs; dr += 1) {
          for (let dc = 0; dc < cs; dc += 1) {
            if (dr === 0 && dc === 0) continue;
            skip[zr1 + dr][zc1 + dc] = true;
          }
        }

        placed.push({ ...rect });
      });

      const RcKey = (r, c) => `${r},${c}`;

      const occupied = new Set();
      for (let cc = 1; cc <= maxGridCols; cc += 1) {
        occupied.add(RcKey(1, cc));
      }
      placed.forEach((rect) => {
        for (let rr = rect.r1; rr <= rect.r2; rr += 1) {
          for (let cc = rect.c1; cc <= rect.c2; cc += 1) {
            occupied.add(RcKey(rr, cc));
          }
        }
      });

      const PixelToGrid = (px, py) => {
        const col = Math.max(
          1,
          Math.min(maxGridCols, Math.floor(px / pxPerCol) + 1)
        );
        const row = Math.max(
          gridFirstRow,
          Math.min(lastGridRow, gridFirstRow + Math.floor(py / pxPerRow))
        );
        return { row, col };
      };

      const PortToPx = (rect, anchor) => {
        const { x, y, w, h } = rect;
        switch (anchor) {
          case 'left':
            return { px: x, py: y + h / 2 };
          case 'right':
            return { px: x + w, py: y + h / 2 };
          case 'top':
            return { px: x + w / 2, py: y };
          case 'bottom':
            return { px: x + w / 2, py: y + h };
          default:
            return { px: x + w / 2, py: y + h / 2 };
        }
      };

      const FindDeptForLine = (idRaw) => {
        if (idRaw == null || idRaw === '') return null;
        const id = String(idRaw);
        const strip = StripDeptPrefix(id);
        return (
          departments.find(
            (de) =>
              String(de.id) === id ||
              String(de.depCd ?? de.DEP_CD) === strip ||
              String(de.depCd ?? de.DEP_CD) === id ||
              StripDeptPrefix(String(de.id)) === strip
          ) || null
        );
      };

      const NearestFreeGrid = (gr, gc) => {
        const tryOne = (r, c) => {
          if (
            r < gridFirstRow ||
            r > lastGridRow ||
            c < 1 ||
            c > maxGridCols
          ) {
            return null;
          }
          if (occupied.has(RcKey(r, c))) return null;
          return { row: r, col: c };
        };
        let t = tryOne(gr, gc);
        if (t) return t;
        for (let rad = 1; rad <= 28; rad += 1) {
          for (let dr = -rad; dr <= rad; dr += 1) {
            for (let dc = -rad; dc <= rad; dc += 1) {
              if (Math.abs(dr) !== rad && Math.abs(dc) !== rad) continue;
              t = tryOne(gr + dr, gc + dc);
              if (t) return t;
            }
          }
        }
        return null;
      };

      const StepTravel = ([fr, fc], [tr, tc]) => {
        if (tr < fr) return 'N';
        if (tr > fr) return 'S';
        if (tc < fc) return 'W';
        if (tc > fc) return 'E';
        return '';
      };

      const MergeLineGlyph = (a, b) => {
        if (!a || a === '') return b;
        if (!b || b === '') return a;
        if (a === b) return a;
        const hasH = a === '─' || b === '─';
        const hasV = a === '│' || b === '│';
        if (hasH && hasV) return '┼';
        return '┼';
      };

      const GlyphForMid = (prev, cur, next) => {
        const tIn = StepTravel(prev, cur);
        const tOut = StepTravel(cur, next);
        if (!tIn || !tOut) return '·';
        if (tIn === tOut) {
          return tIn === 'E' || tIn === 'W' ? '─' : '│';
        }
        const corner = {
          E: { S: '┌', N: '└' },
          W: { S: '┐', N: '┘' },
          S: { E: '└', W: '┘' },
          N: { E: '┌', W: '┐' },
        };
        return corner[tIn]?.[tOut] || '┼';
      };

      const GlyphForEnd = (cur, adj, isStart) => {
        const t = isStart ? StepTravel(cur, adj) : StepTravel(adj, cur);
        if (t === 'E' || t === 'W') return '─';
        if (t === 'N' || t === 'S') return '│';
        return '·';
      };

      const BfsPath = (sr, sc, er, ec) => {
        const sk = RcKey(sr, sc);
        if (sk === RcKey(er, ec)) return [[sr, sc]];
        const prev = new Map();
        prev.set(sk, null);
        const q = [[sr, sc]];
        let qi = 0;
        const neigh = [
          [0, 1],
          [0, -1],
          [1, 0],
          [-1, 0],
        ];
        while (qi < q.length) {
          const [r, c] = q[qi];
          qi += 1;
          if (r === er && c === ec) {
            const path = [];
            let cur = [r, c];
            while (cur) {
              path.push(cur);
              const p = prev.get(RcKey(cur[0], cur[1]));
              cur = p;
            }
            path.reverse();
            return path;
          }
          for (const [dr, dc] of neigh) {
            const nr = r + dr;
            const nc = c + dc;
            if (
              nr < gridFirstRow ||
              nr > lastGridRow ||
              nc < 1 ||
              nc > maxGridCols
            ) {
              continue;
            }
            const nk = RcKey(nr, nc);
            if (occupied.has(nk)) continue;
            if (prev.has(nk)) continue;
            prev.set(nk, [r, c]);
            q.push([nr, nc]);
          }
        }
        return null;
      };

      const lineLayer = new Map();

      lines.forEach((line) => {
        let ga;
        let gb;
        if (
          line.useExplicitCoords === true &&
          Number.isFinite(line.x1) &&
          Number.isFinite(line.y1) &&
          Number.isFinite(line.x2) &&
          Number.isFinite(line.y2)
        ) {
          ga = PixelToGrid(line.x1, line.y1);
          gb = PixelToGrid(line.x2, line.y2);
        } else {
          const sDept = FindDeptForLine(line.sourceId ?? line.source);
          const tDept = FindDeptForLine(line.targetId ?? line.target);
          if (!sDept || !tDept) return;
          const sr = GetDeptRect(sDept);
          const tr = GetDeptRect(tDept);
          const p1 = PortToPx(sr, line.startAnchor ?? null);
          const p2 = PortToPx(tr, line.endAnchor ?? null);
          ga = PixelToGrid(p1.px, p1.py);
          gb = PixelToGrid(p2.px, p2.py);
        }

        const sa = NearestFreeGrid(ga.row, ga.col);
        const sb = NearestFreeGrid(gb.row, gb.col);
        if (!sa || !sb) return;
        const path = BfsPath(sa.row, sa.col, sb.row, sb.col);
        if (!path || path.length < 2) return;

        for (let i = 0; i < path.length; i += 1) {
          const [r, c] = path[i];
          const prev = i > 0 ? path[i - 1] : null;
          const next = i < path.length - 1 ? path[i + 1] : null;
          let g = '·';
          if (!prev) {
            g = GlyphForEnd([r, c], next, true);
          } else if (!next) {
            g = GlyphForEnd([r, c], prev, false);
          } else {
            g = GlyphForMid(prev, [r, c], next);
          }
          const k = RcKey(r, c);
          lineLayer.set(k, MergeLineGlyph(lineLayer.get(k), g));
        }
      });

      lineLayer.forEach((ch, k) => {
        const [rs, cs] = k.split(',');
        const r = Number(rs);
        const c = Number(cs);
        if (occupied.has(k)) return;
        const zr = r - 1;
        const zc = c - 1;
        if (zr < 0 || zc < 0 || zr >= nRows || zc >= nCols) return;
        if (skip[zr][zc]) return;
        if (anchor[zr][zc]) return;
        const cur = tdText[zr][zc];
        if (cur != null && String(cur).trim() !== '') return;
        tdText[zr][zc] = ch;
      });

      const stTip =
        'vertical-align:middle;text-align:left;font-family:맑은 고딕;font-size:10pt;padding:6px;border:1px solid #e2e8f0;background:#ffffff;';
      const stDept =
        'vertical-align:top;text-align:left;font-family:맑은 고딕;font-size:10pt;padding:6px;border:2px solid #94a3b8;background:#f8fafc;';
      const stEmpty =
        'text-align:center;vertical-align:middle;font-family:맑은 고딕;font-size:9pt;color:#334155;border:1px solid #f1f5f9;';

      let html =
        '<table style="border-collapse:collapse;table-layout:fixed;width:100%;">';
      for (let tr = 0; tr < nRows; tr += 1) {
        html += '<tr>';
        for (let tc = 0; tc < nCols; ) {
          if (skip[tr][tc]) {
            tc += 1;
            continue;
          }
          const a = anchor[tr][tc];
          if (a) {
            const kindStyle = a.kind === 'tip' ? stTip : stDept;
            html += `<td rowspan="${a.rs}" colspan="${a.cs}" style="${kindStyle}">${a.text}</td>`;
            tc += a.cs;
          } else {
            const inner = tdText[tr][tc]
              ? EscapeHtml(tdText[tr][tc])
              : '&#160;';
            html += `<td style="${stEmpty}">${inner}</td>`;
            tc += 1;
          }
        }
        html += '</tr>';
      }
      html += '</table>';

      const holder = document.createElement('div');
      holder.innerHTML = html;
      const tbl = holder.querySelector('table');
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.table_to_sheet(tbl);
      /* 열이 너무 좁으면 병합 카드 안 글자가 잘림. 격자는 유지하되 가독성 위해 최소 폭 상향 */
      ws['!cols'] = Array.from({ length: nCols }, () => ({ wch: 1.85 }));
      if (!ws['!rows']) ws['!rows'] = [];
      ws['!rows'][0] = { hpt: 48 };
      XLSX.utils.book_append_sheet(wb, ws, '조직도');
      XLSX.writeFile(
        wb,
        `조직도_${new Date().toISOString().replace(/T/, '_').replace(/\..+/, '').replace(/-/g, '').replace(/:/g, '')}.xlsx`
      );
    } catch (e) {
      console.error('조직도 엑셀 저장 오류:', e);
    }
  }, [props.organizationData]);

  //#endregion

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: '#f6f7f8',
      }}
    >
      {/* 상단 헤더 바 */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #e2e8f0',
          color: '#1e293b',
        }}
      >
        <Toolbar sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#137fec',
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <AccountTreeIcon fontSize="small" />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.125rem',
                fontWeight: 700,
                color: '#1e293b',
                letterSpacing: '-0.015em',
              }}
            >
              안전보건 조직도
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  if (props.onClear) {
                    const result = props.onClear();
                    // Promise를 반환하는 경우 처리
                    if (result && typeof result.then === 'function') {
                      await result;
                    }
                  } else {
                    // onClear가 없으면 빈 Promise 반환
                    return Promise.resolve();
                  }
                } catch (error) {
                  console.error('초기화 중 오류 발생:', error);
                  // 에러 발생 시에도 Promise 반환
                  return Promise.reject(error);
                }
              }}
              sx={{
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#475569',
                '&:hover': {
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.05)',
                },
              }}
            >
              초기화
            </Button>
            {/* 엑셀 다운로드 버튼 임시 비활성화
            <Button
              variant="outlined"
              onClick={DownloadOrganizationExcel}
              name="조직도 엑셀 다운로드"
              sx={{
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#475569',
                '&:hover': {
                  borderColor: '#137fec',
                  color: '#137fec',
                  bgcolor: 'rgba(19, 127, 236, 0.06)',
                },
              }}
            >
              엑셀 다운로드
            </Button>
            */}
            <Button
              variant="contained"
              onClick={async () => {
                const imageDataUrl = await generateOrganizationImage();
                if (imageDataUrl) {
                  const fileName = `조직도_${new Date().toISOString().replace(/T/, '_').replace(/\..+/, '').replace(/-/g, '').replace(/:/g, '')}.png`;
                  downloadImage(imageDataUrl, fileName);
                }
              }}
              name="조직도 다운로드"
              sx={{
                bgcolor: '#137fec',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.875rem',
                px: 2,
                py: 1,
                minWidth: 100,
                '&:hover': {
                  bgcolor: '#0f6fd4',
                },
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* 메인 컨텐츠 영역 — minHeight:0 으로 flex 자식이 뷰포트 밖으로 늘어나지 않게 함 */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* 좌측: 부서 및 인력 목록 */}
        <Box
          sx={{
            width: 320,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
            borderRight: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          {/* 사이드바 헤더 (이미지와 동일: 1행=제목+버튼 세로중앙, 2행=안내문구) */}
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#1e293b',
                }}
              >
                조직 구성원
              </Typography>
              <Box
                sx={{ lineHeight: 0, display: 'flex', alignItems: 'center' }}
              >
                <Tooltip title="추가">
                  <Fab
                    color="primary"
                    size="small"
                    aria-label="추가 메뉴"
                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                    sx={{
                      width: 40,
                      height: 40,
                      minHeight: 40,
                      boxShadow: 1,
                      '&:hover': { boxShadow: 2 },
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </Tooltip>
                <Menu
                  anchorEl={addMenuAnchor}
                  open={Boolean(addMenuAnchor)}
                  onClose={() => setAddMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => {
                      setAddMenuAnchor(null);
                      handleAddDepartmentClick();
                    }}
                  >
                    <ListItemIcon>
                      <BusinessIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>새로운 부서</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAddMenuAnchor(null);
                      handleAddMemberClick();
                    }}
                  >
                    <ListItemIcon>
                      <GroupIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>새로운 구성원</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.75rem', color: '#64748b' }}
            >
              목록에서 드래그하여 배치하세요
            </Typography>
          </Box>

          {/* 탭 */}
          <Box sx={{ borderBottom: '1px solid #e2e8f0', px: 2, flexShrink: 0 }}>
            <Tabs
              value={sidebarTab}
              onChange={(e, newValue) => setSidebarTab(newValue)}
              sx={{
                minHeight: 'auto',
                '& .MuiTab-root': {
                  minHeight: 'auto',
                  py: 1.5,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                },
              }}
            >
              <Tab
                label="부서"
                sx={{
                  fontWeight: sidebarTab === 0 ? 700 : 500,
                  color: sidebarTab === 0 ? '#137fec' : '#64748b',
                  borderBottom:
                    sidebarTab === 0
                      ? '2px solid #137fec'
                      : '2px solid transparent',
                }}
              />
              <Tab
                label="구성원"
                sx={{
                  fontWeight: sidebarTab === 1 ? 700 : 500,
                  color: sidebarTab === 1 ? '#137fec' : '#64748b',
                  borderBottom:
                    sidebarTab === 1
                      ? '2px solid #137fec'
                      : '2px solid transparent',
                }}
              />
            </Tabs>
          </Box>

          {/* 아코디언 영역 */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 0.5,
              py: 1,
              minHeight: 0, // flexbox에서 스크롤이 작동하도록
              maxHeight: '300px', // 최대 높이 제한
            }}
          >
            {sidebarTab === 0 ? (
              // 부서 목록
              <>
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: 'none',
                    border: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}
                    sx={{
                      px: 1.5,
                      py: 1,
                      minHeight: 'auto',
                      '&.Mui-expanded': {
                        minHeight: 'auto',
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#475569',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        부서 정보
                      </Typography>
                      <Chip
                        label={props.departmentList.length}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.625rem',
                          fontWeight: 700,
                          bgcolor: '#e2e8f0',
                          color: '#475569',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0.5, pt: 0 }}>
                    <List dense sx={{ py: 0 }}>
                      {props.departmentList.map((dept, index) => (
                        <ListItem
                          key={`dept_${index}`}
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, dept, 'department')
                          }
                          sx={{
                            p: 1.5,
                            mb: 0.5,
                            bgcolor: dept.isManual === 'Y' ? '#fff3cd' : '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            '&:hover': {
                              borderColor: '#137fec',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            },
                          }}
                        >
                          <DragIcon
                            sx={{
                              color: '#cbd5e1',
                              fontSize: '1.25rem',
                              mr: 1.5,
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                lineHeight: 1.35,
                              }}
                            >
                              {dept.depNmLast}
                              {dept.isManual === 'Y' && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    ml: 1,
                                    color: '#64748b',
                                    fontStyle: 'italic',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  (수기)
                                </Typography>
                              )}
                            </Typography>
                            {/* depNm1~depNm4를 "-"로 연결하여 표시 */}
                            {(() => {
                              const depNms = [
                                dept.depNm1,
                                dept.depNm2,
                                dept.depNm3,
                                dept.depNm4,
                              ].filter((depNm) => depNm && depNm.trim() !== '');

                              if (depNms.length > 0) {
                                return (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.6875rem',
                                      fontWeight: 500,
                                      color: '#64748b',
                                      display: 'block',
                                      mt: 0.25,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere',
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {depNms.join(' - ')}
                                  </Typography>
                                );
                              }
                              return null;
                            })()}
                          </Box>
                          {dept.isManual === 'Y' && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                props.onDepartmentListDelete(dept.deoCd);
                              }}
                              sx={{
                                ml: 1,
                                color: '#ef4444',
                                '&:hover': { bgcolor: '#fee2e2' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>
            ) : (
              // 구성원 목록
              <>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="성명 또는 직책으로 검색"
                  value={sidebarMemberSearchText}
                  onChange={(e) => setSidebarMemberSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ color: '#94a3b8', fontSize: '1.125rem' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: 'none',
                    border: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}
                    sx={{
                      px: 1.5,
                      py: 1,
                      minHeight: 'auto',
                      '&.Mui-expanded': {
                        minHeight: 'auto',
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#475569',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        구성원 정보
                      </Typography>
                      <Chip
                        label={sidebarFilteredMemberList.length}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.625rem',
                          fontWeight: 700,
                          bgcolor: '#e2e8f0',
                          color: '#475569',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0.5, pt: 0 }}>
                    <List dense sx={{ py: 0 }}>
                      {sidebarFilteredMemberList.map((member, _index) => (
                        <ListItem
                          key={_index}
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, member, 'member')
                          }
                          sx={{
                            p: 1.5,
                            mb: 0.5,
                            bgcolor:
                              member.isManual === 'Y' ? '#fff3cd' : '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            '&:hover': {
                              borderColor: '#137fec',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              border: '2px solid #f1f5f9',
                              mr: 1.5,
                            }}
                          >
                            <PersonIcon />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex' }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  truncate: true,
                                }}
                              >
                                {member.name}
                              </Typography>
                              {member.isManual === 'Y' && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    ml: 1,
                                    color: '#64748b',
                                    fontStyle: 'italic',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  (수기)
                                </Typography>
                              )}
                            </Box>

                            {member.position && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.6875rem',
                                  fontWeight: 500,
                                  color: '#137fec',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {member.position}
                              </Typography>
                            )}
                          </Box>
                          <DragIcon
                            sx={{
                              color: '#cbd5e1',
                              fontSize: '1.25rem',
                              '&:hover': { color: '#94a3b8' },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>

                {searchedMembers.length > 0 && (
                  <Accordion
                    defaultExpanded
                    sx={{
                      boxShadow: 'none',
                      border: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}
                      sx={{
                        px: 1.5,
                        py: 1,
                        minHeight: 'auto',
                        '&.Mui-expanded': {
                          minHeight: 'auto',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          검색 결과
                        </Typography>
                        <Chip
                          label={searchedMembers.length}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.625rem',
                            fontWeight: 700,
                            bgcolor: '#e2e8f0',
                            color: '#475569',
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0.5, pt: 0 }}>
                      <List dense sx={{ py: 0 }}>
                        {searchedMembers.map((member, index) => (
                          <ListItem
                            key={`search_${index}`}
                            draggable
                            onDragStart={(e) =>
                              handleDragStart(e, member, 'member')
                            }
                            sx={{
                              p: 1.5,
                              mb: 0.5,
                              bgcolor: '#fff',
                              border: '1px solid #4caf50',
                              borderRadius: 3,
                              cursor: 'grab',
                              '&:active': { cursor: 'grabbing' },
                              '&:hover': {
                                borderColor: '#4caf50',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                border: '2px solid #f1f5f9',
                                mr: 1.5,
                              }}
                            >
                              <PersonIcon />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 700,
                                  color: '#1e293b',
                                }}
                              >
                                {member.NAME || member.name}
                              </Typography>
                              {member.POSITION && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.6875rem',
                                    fontWeight: 500,
                                    color: '#137fec',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {member.POSITION}
                                </Typography>
                              )}
                            </Box>
                            <DragIcon
                              sx={{
                                color: '#cbd5e1',
                                fontSize: '1.25rem',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
          </Box>

          {/* 액션 버튼 (하단 고정) */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              flexShrink: 0, // 항상 보이도록
              flexGrow: 0, // 확장 방지
              bgcolor: '#fff',
              position: 'relative',
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.5,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#475569',
                  }}
                >
                  연결선 타입
                </Typography>
                <Tooltip
                  title={
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ whiteSpace: 'pre-line', lineHeight: 1.55 }}
                    >
                      {ORG_CHART_LINE_HELP_LINES.join('\n')}
                    </Typography>
                  }
                  arrow
                  placement="top"
                >
                  <IconButton size="small" sx={{ color: '#137fec' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ToggleButtonGroup
                value={lineType}
                exclusive
                onChange={(_, newType) => {
                  if (newType !== null) setLineType(newType);
                }}
                size="small"
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    py: 0.75,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&.Mui-selected': {
                      bgcolor: '#137fec',
                      color: '#fff',
                      borderColor: '#137fec',
                    },
                  },
                }}
              >
                <ToggleButton value="straight">직교</ToggleButton>
                <ToggleButton value="smooth">곡선</ToggleButton>
                <ToggleButton value="grid">꺾은선</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: '#f1f5f9',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <Tooltip
              title={
                canvasHelpOpen
                  ? '조작 안내 접기'
                  : ORG_CHART_CANVAS_HELP_LINES.join('\n')
              }
              placement="bottom-start"
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 360,
                    whiteSpace: 'pre-line',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                  },
                },
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={canvasHelpOpen}
                onClick={ToggleCanvasHelp}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    ToggleCanvasHelp();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.65,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: '#e2e8f0' },
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: '1.1rem', color: '#64748b' }} />
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#475569',
                  }}
                >
                  조작 안내
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.68rem', color: '#94a3b8' }}
                >
                  {canvasHelpOpen ? '접기' : '펼치기'}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={
                    canvasHelpOpen ? '조작 안내 접기' : '조작 안내 펼치기'
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    ToggleCanvasHelp();
                  }}
                >
                  {canvasHelpOpen ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
            <Collapse in={canvasHelpOpen}>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  mt: 0,
                  px: 1.5,
                  pb: 1,
                  pl: 3.25,
                  color: '#64748b',
                  fontSize: '0.72rem',
                  lineHeight: 1.5,
                  '& li': { mb: 0.15 },
                  '& li::marker': { color: '#94a3b8' },
                }}
              >
                {ORG_CHART_CANVAS_HELP_LINES.map((line) => (
                  <Typography key={line} component="li" variant="caption">
                    {line}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
          <SAP_BASIC_01_OrgFlow
            ref={orgFlowRef}
            organizationData={props.organizationData}
            onDepartmentAdd={props.onDepartmentAdd}
            onDepartmentMove={props.onDepartmentMove}
            onDepartmentDelete={props.onDepartmentDelete}
            onMemberAssignToDepartment={props.onMemberAssignToDepartment}
            onMemberRemove={props.onMemberRemove}
            onLineAdd={props.onLineAdd}
            onLineDelete={props.onLineDelete}
            lineType={lineType}
            curveness={curveness}
          />
        </Box>
      </Box>


      {/* 부서 추가 다이얼로그 */}
      <Dialog
        title="부서 추가"
        open={departmentDialogOpen}
        onClose={() => setDepartmentDialogOpen(false)}
        contentSx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          p: 3,
          minWidth: 400,
        }}
        fullWidth
        maxWidth="sm"
        useSaveButton={true}
        useCloseButton={true}
        saveButtonName="추가"
        closeButtonName="취소"
        useSaveAfterClose={false}
        onSave={handleDepartmentSave}
        content={
          <Stack spacing={3} sx={{ width: '100%' }}>
            <TextField
              // label="부서명"
              variant="outlined"
              fullWidth
              required
              value={departmentInput.name}
              onChange={(e) =>
                setDepartmentInput({ ...departmentInput, name: e.target.value })
              }
              placeholder="부서명을 입력하세요"
              autoFocus
              sx={{ mt: 3 }}
            />
          </Stack>
        }
      />

      {/* 인력 추가 다이얼로그 */}
      <Dialog
        title="인력 추가"
        open={memberDialogOpen}
        onClose={() => setMemberDialogOpen(false)}
        contentSx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          p: 3,
          minWidth: 400,
        }}
        fullWidth
        maxWidth="sm"
        useSaveButton={true}
        useCloseButton={true}
        saveButtonName="추가"
        closeButtonName="취소"
        useSaveAfterClose={false}
        onSave={handleMemberSave}
        content={
          <Stack spacing={3} sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              fullWidth
              required
              value={memberInput.name}
              onChange={(e) =>
                setMemberInput({ ...memberInput, name: e.target.value })
              }
              placeholder="성명을 입력하세요"
              autoFocus
              sx={{ mt: 1 }}
            />
            <TextField
              variant="outlined"
              fullWidth
              value={memberInput.position}
              onChange={(e) =>
                setMemberInput({ ...memberInput, position: e.target.value })
              }
              placeholder="직책을 입력하세요 (선택사항)"
            />
          </Stack>
        }
      />
    </Box>
  );
}

export default SAP_BASIC_01;
